import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Student } from '@/lib/types';

export function useStudents(personalId: string) {
  const queryClient = useQueryClient();

  const { data: students = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['students', personalId],
    queryFn: async () => {
      console.log('[useStudents] query running personalId=', personalId);
      // 1) Lista de students do PT
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('personal_id', personalId)
        .eq('is_self', false)
        .order('full_name');
      console.log('[useStudents] students count=', data?.length, 'error=', error);
      if (error) throw error;
      if (!data || data.length === 0) return [] as Student[];

      // 2) Enriquece com last_seen_at via 2a query em profiles (evita embed via FK
      //    ambigua — students tem 2 FKs em profiles, PostgREST falha silenciosamente).
      //    RLS permite o PT ler profiles dos seus alunos (policy personal_reads_student_profiles).
      const profileIds = Array.from(new Set(
        (data as Student[]).map(s => s.profile_id).filter((id): id is string => !!id)
      ));
      const lastSeenMap = new Map<string, string | null>();
      if (profileIds.length > 0) {
        const { data: profileRows, error: pErr } = await supabase
          .from('profiles')
          .select('id, last_seen_at')
          .in('id', profileIds);
        console.log('[useStudents] profiles count=', profileRows?.length, 'error=', pErr);
        if (!pErr) {
          for (const p of (profileRows ?? []) as { id: string; last_seen_at: string | null }[]) {
            lastSeenMap.set(p.id, p.last_seen_at);
          }
        }
      }
      return (data as Student[]).map(s => ({
        ...s,
        last_seen_at: s.profile_id ? lastSeenMap.get(s.profile_id) ?? null : null,
      })) as Student[];
    },
    enabled: !!personalId,
  });

  const { mutateAsync: updateStudent } = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Omit<Student, 'personal_id' | 'profile_id' | 'created_at'>> & { id: string }) => {
      const { data, error } = await supabase
        .from('students')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as Student;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['students', personalId] }),
  });

  const { mutateAsync: updateStudentStatus } = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'waiting' | 'active' | 'inactive' }) => {
      const { error } = await supabase.from('students').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['students', personalId] }),
  });

  return { students, loading, updateStudent, updateStudentStatus, refetch };
}
